import React, { useCallback, useEffect, useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { Capacitor } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import Modal from '../modal/Modal'
import messages from './NotificationGate.messages'
import {
    NotificationPermissionState,
    checkNotificationPermission,
    requestNotificationPermission,
    openAppNotificationSettings,
} from '../../services/notifications/notificationPermission'

export interface IProps {
    open: boolean
    /** Permission state that caused the gate to open ('prompt' or 'denied'). */
    state: NotificationPermissionState
    onClose: () => void
    /** Called once permission becomes 'granted' so login can proceed. */
    onGranted: () => void
}

const NotificationGate: React.FC<IProps> = ({ open, state, onClose, onGranted }) => {
    const [currentState, setCurrentState] = useState<NotificationPermissionState>(state)
    const [busy, setBusy] = useState(false)

    // Reset to the incoming state whenever the gate is (re)opened.
    useEffect(() => {
        if (open) {
            setCurrentState(state)
        }
    }, [open, state])

    const applyState = useCallback(
        (next: NotificationPermissionState) => {
            setCurrentState(next)
            if (next === 'granted') {
                onGranted()
            }
        },
        [onGranted]
    )

    // Re-check permission when the user returns from the OS settings screen.
    useEffect(() => {
        if (!open || !Capacitor.isNativePlatform()) {
            return
        }

        let handle: PluginListenerHandle | undefined
        let cancelled = false

        App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                checkNotificationPermission().then(applyState)
            }
        }).then((h) => {
            if (cancelled) {
                h.remove()
            } else {
                handle = h
            }
        })

        return () => {
            cancelled = true
            handle?.remove()
        }
    }, [open, applyState])

    const handlePrimary = async () => {
        setBusy(true)
        try {
            if (currentState === 'prompt') {
                // Still promptable: show the OS dialog.
                applyState(await requestNotificationPermission())
            } else {
                // Denied: the OS will not re-prompt, so deep-link to settings.
                // The appStateChange listener re-checks when the user returns.
                await openAppNotificationSettings()
            }
        } finally {
            setBusy(false)
        }
    }

    const isDenied = currentState === 'denied'
    const stepsMessage = Capacitor.getPlatform() === 'ios' ? messages.stepsIos : messages.stepsAndroid

    return (
        <Modal
            modalName='notification-gate'
            open={open}
            onClose={onClose}
            header={() => (
                <Typography>
                    <FormattedMessage {...messages.title} />
                </Typography>
            )}
        >
            <Stack spacing={2}>
                <Typography>
                    <FormattedMessage {...(isDenied ? messages.bodyDenied : messages.bodyPrompt)} />
                </Typography>

                {isDenied ? (
                    <Typography sx={{ whiteSpace: 'pre-line' }} color='text.secondary'>
                        <FormattedMessage {...stepsMessage} />
                    </Typography>
                ) : null}

                <Button
                    color='primary'
                    variant='contained'
                    onClick={handlePrimary}
                    disabled={busy}
                    fullWidth
                >
                    <FormattedMessage {...(isDenied ? messages.openSettings : messages.enable)} />
                </Button>

                <Button color='inherit' variant='text' onClick={onClose} disabled={busy} fullWidth>
                    <FormattedMessage {...messages.cancel} />
                </Button>
            </Stack>
        </Modal>
    )
}

export default NotificationGate
